import type { CreasePattern, CreaseAssignment } from '@/origami/types';
import { isCut, isFoldable } from '@/origami/types';
import { resolveTargetAngle } from '@/origami/angles';
import { triangulatePolygon } from '@/geometry/triangulate';
import { deriveTopology } from '@/origami/topology';
import { dist2 } from '@/geometry/vec2';

/**
 * The simulation mesh: a triangulated, cut-aware version of the crease pattern
 * with bars (edges) and hinges (dihedral joints).
 *
 * Design rules:
 *  - Triangulation never moves or adds vertices, so pattern vertices map 1:1
 *    onto simulation vertices *within a connected piece*. Cuts duplicate them.
 *  - Auxiliary (triangulation) edges are hinges with target angle 0 and their
 *    own stiffness. They are NOT creases and are never exported to FOLD as such.
 *  - All arrays are TypedArrays so the mesh can be transferred to a Worker.
 */
export interface SimulationMesh {
  vertexCount: number;
  /** Flat rest positions, xyz triples, z = 0. */
  restPositions: Float64Array;
  /** Triangle vertex indices, CCW in the flat state. */
  triangles: Uint32Array;
  triangleCount: number;

  /** Bar constraints: pairs of vertex ids. */
  barVertices: Uint32Array; // 2 * barCount
  barRestLength: Float64Array;
  barCount: number;
  /** 1 for bars lying on a real pattern edge, 0 for triangulation diagonals. */
  barIsPatternEdge: Uint8Array;

  /** Hinges: [v1, v2, apexA, apexB] per hinge. */
  hingeVertices: Uint32Array; // 4 * hingeCount
  hingeCount: number;
  /** Target fold angle at 100% fold, radians. Auxiliary hinges target 0. */
  hingeTargetAngle: Float64Array;
  /** Rest (flat-pattern) dihedral angle; always 0 for a flat sheet. */
  hingeRestAngle: Float64Array;
  /** Pattern edge id, or -1 for auxiliary hinges. */
  hingePatternEdge: Int32Array;
  hingeAssignment: Uint8Array; // index into HINGE_KIND
  /** Per-hinge stiffness multiplier (user editable). */
  hingeStiffness: Float64Array;
  /** 1 when the crease angle is pinned by the user. */
  hingeLocked: Uint8Array;

  /** Mapping simulation vertex -> pattern vertex. */
  vertexPatternIndex: Int32Array;
  /** Mapping pattern edge -> hinge id, or -1. */
  patternEdgeHinge: Int32Array;
  /** Connected piece id per vertex (kirigami pieces). */
  vertexPiece: Int32Array;
  pieceCount: number;
  /** Lumped vertex mass (sum of 1/3 of incident triangle areas). */
  vertexMass: Float64Array;
  /** Triangle -> pattern face id. */
  trianglePatternFace: Int32Array;
}

export const HINGE_KIND: CreaseAssignment[] = ['B', 'M', 'V', 'F', 'U', 'C', 'J'];
const kindIndex = (a: CreaseAssignment): number => Math.max(0, HINGE_KIND.indexOf(a));

class UnionFind {
  private parent: Int32Array;
  constructor(n: number) {
    this.parent = new Int32Array(n);
    for (let i = 0; i < n; i++) this.parent[i] = i;
  }
  find(a: number): number {
    let r = a;
    while (this.parent[r] !== r) r = this.parent[r];
    while (this.parent[a] !== r) {
      const next = this.parent[a];
      this.parent[a] = r;
      a = next;
    }
    return r;
  }
  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[rb] = ra;
  }
}

export interface BuildMeshResult {
  mesh: SimulationMesh;
  warnings: string[];
}

export function buildSimulationMesh(pattern: CreasePattern): BuildMeshResult {
  const warnings: string[] = [];
  const topo = deriveTopology(pattern);
  const faces = pattern.facesVertices;

  // --- 1. Split the sheet into connected pieces across cut edges. ---
  const uf = new UnionFind(Math.max(faces.length, 1));
  for (let e = 0; e < pattern.edgesVertices.length; e++) {
    if (isCut(pattern.edgesAssignment[e])) continue;
    const [l, r] = topo.edgeFaces[e];
    if (l >= 0 && r >= 0) uf.union(l, r);
  }
  const pieceOfFace = new Int32Array(faces.length);
  const pieceIds = new Map<number, number>();
  for (let f = 0; f < faces.length; f++) {
    const root = uf.find(f);
    let id = pieceIds.get(root);
    if (id === undefined) {
      id = pieceIds.size;
      pieceIds.set(root, id);
    }
    pieceOfFace[f] = id;
  }
  const pieceCount = Math.max(pieceIds.size, 1);

  // --- 2. Allocate simulation vertices per (piece, pattern vertex). ---
  const vertexKey = new Map<string, number>();
  const vertexPatternIndex: number[] = [];
  const vertexPiece: number[] = [];
  const positions: number[] = [];
  const simVertex = (piece: number, pv: number): number => {
    const key = `${piece}:${pv}`;
    const existing = vertexKey.get(key);
    if (existing !== undefined) return existing;
    const id = vertexPatternIndex.length;
    vertexKey.set(key, id);
    vertexPatternIndex.push(pv);
    vertexPiece.push(piece);
    const p = pattern.verticesCoords[pv];
    positions.push(p.x, p.y, 0);
    return id;
  };

  // --- 3. Triangulate every face. ---
  const triangles: number[] = [];
  const trianglePatternFace: number[] = [];
  for (let f = 0; f < faces.length; f++) {
    const loop = faces[f];
    const tris = triangulatePolygon(pattern.verticesCoords, loop);
    if (tris.length === 0 && loop.length >= 3) {
      warnings.push(`Face ${f} could not be triangulated (degenerate or self-intersecting).`);
      continue;
    }
    const piece = pieceOfFace[f];
    for (const t of tris) {
      triangles.push(simVertex(piece, t[0]), simVertex(piece, t[1]), simVertex(piece, t[2]));
      trianglePatternFace.push(f);
    }
  }

  const vertexCount = vertexPatternIndex.length;
  const triangleCount = triangles.length / 3;

  // --- 4. Bars and hinges from the triangle soup. ---
  interface DirectedRec {
    tri: number;
    apex: number;
  }
  const directed = new Map<string, DirectedRec>();
  const barMap = new Map<string, { i: number; j: number }>();
  for (let t = 0; t < triangleCount; t++) {
    const a = triangles[t * 3];
    const b = triangles[t * 3 + 1];
    const c = triangles[t * 3 + 2];
    const tri: [number, number, number][] = [
      [a, b, c],
      [b, c, a],
      [c, a, b],
    ];
    for (const [i, j, apex] of tri) {
      directed.set(`${i}_${j}`, { tri: t, apex });
      const key = i < j ? `${i}_${j}` : `${j}_${i}`;
      if (!barMap.has(key)) barMap.set(key, { i: Math.min(i, j), j: Math.max(i, j) });
    }
  }

  // Which sim-vertex pair corresponds to which pattern edge?
  const patternEdgeKey = new Map<string, number>();
  for (let e = 0; e < pattern.edgesVertices.length; e++) {
    const [a, b] = pattern.edgesVertices[e];
    patternEdgeKey.set(a < b ? `${a}_${b}` : `${b}_${a}`, e);
  }

  const barVertices: number[] = [];
  const barRestLength: number[] = [];
  const barIsPatternEdge: number[] = [];
  for (const { i, j } of barMap.values()) {
    barVertices.push(i, j);
    barRestLength.push(
      dist2(
        pattern.verticesCoords[vertexPatternIndex[i]],
        pattern.verticesCoords[vertexPatternIndex[j]],
      ),
    );
    const pKey = (() => {
      const pa = vertexPatternIndex[i];
      const pb = vertexPatternIndex[j];
      return pa < pb ? `${pa}_${pb}` : `${pb}_${pa}`;
    })();
    barIsPatternEdge.push(patternEdgeKey.has(pKey) ? 1 : 0);
  }

  const hingeVertices: number[] = [];
  const hingeTargetAngle: number[] = [];
  const hingeRestAngle: number[] = [];
  const hingePatternEdge: number[] = [];
  const hingeAssignment: number[] = [];
  const hingeStiffness: number[] = [];
  const hingeLocked: number[] = [];
  const patternEdgeHinge = new Int32Array(pattern.edgesVertices.length).fill(-1);

  for (const { i, j } of barMap.values()) {
    const fwd = directed.get(`${i}_${j}`);
    const bwd = directed.get(`${j}_${i}`);
    if (!fwd || !bwd) continue; // boundary of the sheet: no dihedral
    const pa = vertexPatternIndex[i];
    const pb = vertexPatternIndex[j];
    const pKey = pa < pb ? `${pa}_${pb}` : `${pb}_${pa}`;
    const patternEdge = patternEdgeKey.get(pKey);

    let assignment: CreaseAssignment = 'J';
    let target = 0;
    if (patternEdge !== undefined) {
      assignment = pattern.edgesAssignment[patternEdge];
      if (!isFoldable(assignment)) continue; // cut or boundary: no hinge
      target = resolveTargetAngle(assignment, pattern.edgesFoldAngle[patternEdge]);
      patternEdgeHinge[patternEdge] = hingeTargetAngle.length;
    }

    hingeVertices.push(i, j, fwd.apex, bwd.apex);
    hingeTargetAngle.push(target);
    hingeRestAngle.push(0);
    hingePatternEdge.push(patternEdge ?? -1);
    hingeAssignment.push(kindIndex(assignment));
    hingeStiffness.push(1);
    hingeLocked.push(0);
  }

  // --- 5. Lumped masses from triangle areas. ---
  const vertexMass = new Float64Array(vertexCount).fill(0);
  for (let t = 0; t < triangleCount; t++) {
    const a = triangles[t * 3];
    const b = triangles[t * 3 + 1];
    const c = triangles[t * 3 + 2];
    const ax = positions[a * 3];
    const ay = positions[a * 3 + 1];
    const bx = positions[b * 3];
    const by = positions[b * 3 + 1];
    const cx = positions[c * 3];
    const cy = positions[c * 3 + 1];
    const area = Math.abs((bx - ax) * (cy - ay) - (by - ay) * (cx - ax)) / 2;
    vertexMass[a] += area / 3;
    vertexMass[b] += area / 3;
    vertexMass[c] += area / 3;
  }
  let totalMass = 0;
  for (let i = 0; i < vertexCount; i++) totalMass += vertexMass[i];
  if (totalMass > 0) {
    // Normalise so the average vertex mass is 1: keeps solver scales sane
    // regardless of paper size.
    const scale = vertexCount / totalMass;
    for (let i = 0; i < vertexCount; i++) vertexMass[i] *= scale;
  } else {
    vertexMass.fill(1);
  }
  for (let i = 0; i < vertexCount; i++) {
    if (!(vertexMass[i] > 1e-12)) vertexMass[i] = 1e-12;
  }

  const mesh: SimulationMesh = {
    vertexCount,
    restPositions: Float64Array.from(positions),
    triangles: Uint32Array.from(triangles),
    triangleCount,
    barVertices: Uint32Array.from(barVertices),
    barRestLength: Float64Array.from(barRestLength),
    barCount: barRestLength.length,
    barIsPatternEdge: Uint8Array.from(barIsPatternEdge),
    hingeVertices: Uint32Array.from(hingeVertices),
    hingeCount: hingeTargetAngle.length,
    hingeTargetAngle: Float64Array.from(hingeTargetAngle),
    hingeRestAngle: Float64Array.from(hingeRestAngle),
    hingePatternEdge: Int32Array.from(hingePatternEdge),
    hingeAssignment: Uint8Array.from(hingeAssignment),
    hingeStiffness: Float64Array.from(hingeStiffness),
    hingeLocked: Uint8Array.from(hingeLocked),
    vertexPatternIndex: Int32Array.from(vertexPatternIndex),
    patternEdgeHinge,
    vertexPiece: Int32Array.from(vertexPiece),
    pieceCount,
    vertexMass,
    trianglePatternFace: Int32Array.from(trianglePatternFace),
  };

  if (vertexCount === 0) warnings.push('The pattern produced no simulation vertices.');
  return { mesh, warnings };
}
